/**
 * DataTableCard Component
 *
 * Reusable card component for displaying data tables with optional server-side pagination.
 * More flexible than StatsTableCard - supports custom headers, icons, and pagination.
 *
 * Features:
 * - Optional icon box with gradient background
 * - Title and optional subtitle
 * - Optional count chip
 * - Skeleton loading states
 * - Server-side or client-side pagination
 * - Search functionality
 * - Hover effects and smooth animations
 * - Supports generic data types
 */
import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TablePagination,
} from "@mui/material";
import { SvgIconComponent, CalendarToday } from "@mui/icons-material";
import DataTable, { TableColumn } from "./DataTable";
import { TableSkeleton } from "./common";
import { boxShadows } from "../utils/theme";

interface DataTableCardProps<T extends Record<string, any>> {
  /** Title displayed in the header */
  title: string;
  /** Optional subtitle displayed below the title */
  subtitle?: string;
  /** Optional icon component to display in header */
  icon?: SvgIconComponent;
  /** Icon size (default: 20 for icon box, 28 for plain icon) */
  iconSize?: number;
  /** Icon color (default: white for icon box, #1a237e for plain icon) */
  iconColor?: string;
  /** Whether to show icon in a gradient box (default: false) */
  showIconBox?: boolean;
  /** Gradient for icon box background */
  iconBoxGradient?: string;
  /** Array of data items to display in the table */
  data: T[];
  /** Column definitions for the table */
  columns: TableColumn<T>[];
  /** Optional count chip configuration (can also be simple string for "X records" format) */
  countChip?:
    | {
        label: string;
        color?:
          | "primary"
          | "default"
          | "secondary"
          | "error"
          | "info"
          | "success"
          | "warning";
        variant?: "filled" | "outlined";
        showIcon?: boolean; // Show calendar icon in chip
      }
    | string; // Simple string like "records" will show "X records"
  /** Header background (gradient or solid color, default: subtle gradient) */
  headerBackground?: string;
  /** Empty state configuration */
  emptyState: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
  };
  /** Loading state */
  isLoading?: boolean;
  /** Fetching state (shows skeleton) */
  isFetching?: boolean;
  /** Enable search functionality (default: true) */
  enableSearch?: boolean;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Columns to search in */
  searchableColumns?: string[];
  /** Show table headers even when empty (default: true) */
  showTableOnEmpty?: boolean;
  /** Server-side pagination configuration (optional, no pagination if not provided) */
  pagination?: {
    page: number;
    limit: number;
    total: number;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    rowsPerPageOptions?: number[];
  };
  /** TableSkeleton configuration */
  skeletonConfig?: {
    rows?: number;
    showAvatar?: boolean;
    textColumns?: number;
    chipColumns?: number;
    showIconHeader?: boolean;
    headerWidth?: number;
  };
}

/**
 * DataTableCard - Reusable data table card with optional pagination
 *
 * @example
 * ```tsx
 * // Stats table style (like StatsTableCard)
 * <DataTableCard
 *   title="Daily Statistics"
 *   icon={Assessment}
 *   showIconBox={true}  // Shows icon in gradient box
 *   data={dailyData}
 *   columns={dailyColumns}
 *   countChip="records"  // Will show "X records" with calendar icon
 *   emptyState={{
 *     icon: <Icon />,
 *     title: "No data",
 *     subtitle: "Try different filters"
 *   }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Data table style with pagination
 * <DataTableCard
 *   title="Payment Transactions"
 *   subtitle="All parking payments"
 *   icon={PaymentIcon}
 *   iconSize={28}
 *   iconColor="#1a237e"
 *   data={payments}
 *   columns={paymentColumns}
 *   countChip={{
 *     label: `${total} Total`,
 *     color: "primary",
 *     variant: "outlined"
 *   }}
 *   pagination={{
 *     page: currentPage,
 *     limit: pageSize,
 *     total: totalRecords,
 *     onPageChange: setPage,
 *     onLimitChange: setLimit,
 *   }}
 *   enableSearch
 *   searchPlaceholder="Search transactions..."
 *   emptyState={{ ... }}
 * />
 * ```
 */
export const DataTableCard = <T extends Record<string, any>>({
  title,
  subtitle,
  icon: Icon,
  iconSize,
  iconColor,
  showIconBox = false,
  iconBoxGradient = "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
  data,
  columns,
  countChip,
  headerBackground = "linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.08) 100%)",
  emptyState,
  isLoading = false,
  isFetching = false,
  enableSearch = true,
  searchPlaceholder,
  searchableColumns,
  showTableOnEmpty = true,
  pagination,
  skeletonConfig,
}: DataTableCardProps<T>) => {
  // Determine icon size and color based on showIconBox
  const finalIconSize = iconSize ?? (showIconBox ? 20 : 28);
  const finalIconColor = iconColor ?? (showIconBox ? "white" : "#1a237e");

  // Process countChip prop - support both string and object formats
  const countChipConfig =
    typeof countChip === "string"
      ? {
          label: `${data.length || 0} ${countChip}`,
          color: "primary" as const,
          variant: "outlined" as const,
          showIcon: showIconBox, // Show calendar icon if using icon box style
        }
      : countChip;
  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.08)",
        overflow: "hidden",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: boxShadows.medium,
          borderColor: "rgba(102,126,234,0.2)",
        },
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {isLoading || isFetching ? (
          <TableSkeleton
            rows={skeletonConfig?.rows || 5}
            showAvatar={skeletonConfig?.showAvatar}
            textColumns={skeletonConfig?.textColumns || 3}
            chipColumns={skeletonConfig?.chipColumns || 2}
            showIconHeader={skeletonConfig?.showIconHeader}
            headerWidth={skeletonConfig?.headerWidth}
          />
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                p: 3,
                pb: showIconBox ? 2 : 3, // Less bottom padding for icon box style
                borderBottom: showIconBox ? "2px solid" : "1px solid",
                borderColor: "divider",
                background: headerBackground,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: showIconBox ? 1 : 2,
                  }}
                >
                  {/* Icon */}
                  {Icon && (
                    <>
                      {showIconBox ? (
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            background: iconBoxGradient,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon
                            sx={{
                              fontSize: finalIconSize,
                              color: finalIconColor,
                            }}
                          />
                        </Box>
                      ) : (
                        <Icon
                          sx={{
                            fontSize: finalIconSize,
                            color: finalIconColor,
                          }}
                        />
                      )}
                    </>
                  )}

                  {/* Title & Subtitle */}
                  <Box>
                    <Typography variant="h6" component="h2" fontWeight="bold">
                      {title}
                    </Typography>
                    {subtitle && (
                      <Typography variant="body2" color="text.secondary">
                        {subtitle}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Optional Count Chip */}
                {countChipConfig && (
                  <Chip
                    icon={
                      countChipConfig.showIcon ? <CalendarToday /> : undefined
                    }
                    label={countChipConfig.label}
                    color={countChipConfig.color || "primary"}
                    variant={countChipConfig.variant || "outlined"}
                    sx={{
                      fontWeight: 600,
                      ...(showIconBox && {
                        bgcolor: "rgba(102,126,234,0.12)",
                        color: "#1a237e",
                        border: "1px solid rgba(102,126,234,0.25)",
                      }),
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Table Content */}
            <Box sx={{ p: pagination ? 2 : 3, pt: showIconBox ? 2 : 3 }}>
              <DataTable
                data={data}
                columns={columns}
                isLoading={false}
                enablePagination={false} // Server-side pagination handled separately
                enableSearch={enableSearch}
                searchPlaceholder={searchPlaceholder}
                searchableColumns={searchableColumns}
                emptyStateIcon={emptyState.icon}
                emptyStateTitle={emptyState.title}
                emptyStateSubtitle={emptyState.subtitle}
                showTableOnEmpty={showTableOnEmpty}
              />

              {/* Server-side pagination controls */}
              {pagination && pagination.total > 0 && (
                <TablePagination
                  component="div"
                  count={pagination.total}
                  page={pagination.page - 1} // MUI uses 0-based indexing
                  onPageChange={(event: unknown, newPage: number) => {
                    pagination.onPageChange(newPage + 1); // Convert back to 1-based
                  }}
                  rowsPerPage={pagination.limit}
                  onRowsPerPageChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                  ) => {
                    pagination.onLimitChange(parseInt(event.target.value, 10));
                    pagination.onPageChange(1); // Reset to first page
                  }}
                  rowsPerPageOptions={
                    pagination.rowsPerPageOptions || [10, 20, 30, 40, 50]
                  }
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    ".MuiTablePagination-toolbar": {
                      px: 2,
                    },
                  }}
                />
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DataTableCard;
